import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import type {
	CreateNotificationPushDTO,
	NotificationPushDTO,
	NotificationPushListResponseDTO,
} from '@libs/dtos/notifications/notification-push-internal.dto';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/** GET list: `{ data, total, page, limit, totalPages }` like every paginated endpoint */
export type NotificationPushPage = NotificationPushListResponseDTO;

export interface NotificationPushCount {
	total: number;
	unread: number;
}

/** Result of the read-all / delete-all endpoints */
export interface NotificationPushBatchResult {
	count: number;
}

/** Result of the bulk endpoints over a list of ids */
export interface NotificationPushBulkResult {
	affected: number;
}

/**
 * Internal notifications of the logged-in user. The backend takes the user from the session token,
 * so no call sends a user id.
 */
@Injectable({
	providedIn: 'root',
})
export class NotificationPushInternalService {
	private baseUrl = `${environment.pathBackend}/notification-push-internal`;

	constructor(private http: HttpClient) {}

	/** Latest 50 notifications of the current user */
	getAllNotificationPush(): Observable<NotificationPushPage> {
		const params = new HttpParams().set('limit', '50').set('page', '1');
		return this.http
			.get<NotificationPushPage>(this.baseUrl, { params })
			.pipe(catchError(() => throwError(() => new Error('Failed to load notifications'))));
	}

	/** Notification counts (total and unread) */
	getNotificationPushCount(): Observable<NotificationPushCount> {
		return this.http
			.get<NotificationPushCount>(`${this.baseUrl}/count`)
			.pipe(catchError(() => throwError(() => new Error('Failed to load notification counts'))));
	}

	/** Creates a notification (for the current user unless the data names another one) */
	createNotificationPush(notificationData: CreateNotificationPushDTO): Observable<NotificationPushDTO> {
		return this.http
			.post<NotificationPushDTO>(this.baseUrl, notificationData)
			.pipe(catchError(() => throwError(() => new Error('Failed to create notification'))));
	}

	markAsReadNotificationPush(id: number): Observable<NotificationPushDTO> {
		return this.http
			.post<NotificationPushDTO>(`${this.baseUrl}/${id}/read`, {})
			.pipe(catchError(() => throwError(() => new Error('Failed to mark notification as read'))));
	}

	/** Marks several of the user's notifications as read in one request */
	bulkMarkAsReadNotificationPush(ids: number[]): Observable<NotificationPushBulkResult> {
		return this.http
			.post<NotificationPushBulkResult>(`${this.baseUrl}/bulk-read`, { ids })
			.pipe(catchError(() => throwError(() => new Error('Failed to mark notifications as read'))));
	}

	markAllAsReadNotificationPush(): Observable<NotificationPushBatchResult> {
		return this.http
			.post<NotificationPushBatchResult>(`${this.baseUrl}/read-all`, {})
			.pipe(catchError(() => throwError(() => new Error('Failed to mark all notifications as read'))));
	}

	deleteNotificationPush(id: number): Observable<NotificationPushDTO> {
		return this.http
			.delete<NotificationPushDTO>(`${this.baseUrl}/${id}`)
			.pipe(catchError(() => throwError(() => new Error('Failed to delete notification'))));
	}

	/** Deletes several of the user's notifications in one request */
	bulkDeleteNotificationPush(ids: number[]): Observable<NotificationPushBulkResult> {
		return this.http
			.post<NotificationPushBulkResult>(`${this.baseUrl}/bulk-delete`, { ids })
			.pipe(catchError(() => throwError(() => new Error('Failed to delete notifications'))));
	}

	deleteAllNotificationsPush(): Observable<NotificationPushBatchResult> {
		return this.http
			.delete<NotificationPushBatchResult>(this.baseUrl)
			.pipe(catchError(() => throwError(() => new Error('Failed to delete all notifications'))));
	}
}
